import React, { Component } from "react";
import {
  Button,
  Card,
  Row,
  Col,
  Form,
  InputGroup,
  Alert,
} from "react-bootstrap";
import axios from "axios";

class SensorConfigPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      config: this.props.config || {
        id: "",
        assetId: this.props.assetId,
        sensorType: this.props.sensorType,
        isEnabled: false,
        engineStatus: "",
        fridgeStatus: "",
        lowTemp: 0,
        highTemp: 0,
        durationThresholdMinutes: 0,
        activeDays: "Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday",
        startTime: "00:00",
        endTime: "23:59",
        emails: "",
      },
      emailInput: "",
      loading: true,
      saveSuccess: false,
      errorMessage: "",
      isUpdate: false,
    };
  }

  componentDidMount() {
    this.loadConfig();
  }

  componentDidUpdate(prevProps) {
    // If assetId or sensorType changes, reload the config
    if (
      prevProps.assetId !== this.props.assetId ||
      prevProps.sensorType !== this.props.sensorType
    ) {
      this.loadConfig();
    }
  }

  loadConfig = async () => {
    try {
      this.setState({ loading: true });
      const { assetId, sensorType } = this.props;

      const response = await axios.get(
        `/api/assettempalert/${assetId}/${sensorType}`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      this.setState({
        config: {
          activeDays: "Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday",
          startTime: "00:00",
          endTime: "23:59",
          ...response.data,
        },
        loading: false,
      });
    } catch (error) {
      console.error(
        `Error loading configuration for sensor ${this.props.sensorType}:`,
        error
      );
      this.setState({ loading: false });
    }
  };

  handleEnableChange = (e) => {
    const isEnabled = e.target.checked;
    this.setState((prevState) => ({
      config: {
        ...prevState.config,
        isEnabled: isEnabled,
      },
    }));
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      config: {
        ...prevState.config,
        [name]: name === "isEnabled" ? e.target.checked : value,
      },
    }));
  };

  handleEmailInputChange = (e) => {
    this.setState({ emailInput: e.target.value });
  };

  handleDayToggle = (day) => {
    this.setState((prevState) => {
      const selected = (prevState.config.activeDays || "")
        .split(",")
        .filter(Boolean);
      const activeDays = selected.includes(day)
        ? selected.filter((value) => value !== day)
        : [...selected, day];
      return { config: { ...prevState.config, activeDays: activeDays.join(",") } };
    });
  };

  addEmail = () => {
    const email = this.state.emailInput.trim();
    if (!email) return;

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.setState({ errorMessage: "Please enter a valid email address" });
      return;
    }

    const currentEmails = this.state.config.emails
      ? this.state.config.emails.split(",").map((e) => e.trim())
      : [];

    if (!currentEmails.includes(email)) {
      const newEmails = [...currentEmails, email].join(",");

      this.setState((prevState) => ({
        config: {
          ...prevState.config,
          emails: newEmails,
        },
        emailInput: "",
        errorMessage: "",
      }));
    } else {
      this.setState({ errorMessage: "This email is already in the list" });
    }
  };

  removeEmail = (emailToRemove) => {
    const emails = this.state.config.emails
      .split(",")
      .filter((email) => email.trim() !== emailToRemove.trim())
      .join(",");

    this.setState((prevState) => ({
      config: {
        ...prevState.config,
        emails: emails,
      },
    }));
  };

  saveConfig = async () => {
    const { config } = this.state;
    const isUpdate = Boolean(config.id);

    this.setState({
      saveSuccess: false,
      errorMessage: "",
      isUpdate: isUpdate,
    });

    if (config.isEnabled) {
      // Validate inputs when enabled
      if (parseFloat(config.lowTemp) >= parseFloat(config.highTemp)) {
        this.setState({
          errorMessage: "High temperature must be greater than low temperature",
        });
        return;
      }

      if (parseInt(config.durationThresholdMinutes) <= 0) {
        this.setState({
          errorMessage: "Duration must be greater than 0 minutes",
        });
        return;
      }

      if (!config.emails) {
        this.setState({
          errorMessage: "At least one email address is required",
        });
        return;
      }

      if (!config.activeDays) {
        this.setState({ errorMessage: "Select at least one active day" });
        return;
      }
    }

    try {
      // Sử dụng một API duy nhất cho cả tạo mới và cập nhật
      await axios.post("/api/assettempalert/save", config, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });

      this.setState({
        saveSuccess: true,
        errorMessage: "",
      });

      this.loadConfig();

      // Notify parent that we've updated
      if (this.props.onConfigUpdated) {
        this.props.onConfigUpdated();
      }
    } catch (error) {
      console.error("Error saving configuration");
      this.setState({
        errorMessage: "Failed to save configuration. Please try again.",
      });
    }
  };

  cancelChanges = () => {
    this.loadConfig();
    this.setState({
      saveSuccess: false,
      errorMessage: "",
    });
  };

  renderEmailList = () => {
    const { config } = this.state;

    if (!config.emails) return null;

    return (
      <div className="email-list">
        {config.emails.split(",").map(
          (email, index) =>
            email.trim() && (
              <div key={index} className="email-tag">
                {email.trim()}
                <Button
                  variant="link"
                  size="sm"
                  className="remove-email"
                  onClick={() => this.removeEmail(email)}
                >
                  ×
                </Button>
              </div>
            )
        )}
      </div>
    );
  };

  render() {
    const { config, emailInput, loading, saveSuccess, errorMessage } =
      this.state;
    const { sensorLabel } = this.props;

    if (loading) {
      return <div className="loading-indicator">Loading configuration...</div>;
    }

    return (
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <h5>{sensorLabel}</h5>
            <p className="text-muted">
              Configure temperature monitoring and alert settings
            </p>
          </div>
          <Form.Check
            type="switch"
            id={`sensor-${this.props.sensorType}-enabled`}
            label={`${config.isEnabled ? "Enabled" : "Disabled"}`}
            checked={config.isEnabled}
            onChange={this.handleEnableChange}
          />
        </Card.Header>
        <Card.Body>
          {saveSuccess && (
            <Alert variant="success" className="mb-3">
              {this.state.isUpdate ? 
                "Alert configuration saved successfully! Your new settings will override any previous configuration and reset current alerts." :
                "Alert configuration saved successfully!"
              }
            </Alert>
          )}
          {errorMessage && (
            <Alert variant="danger" className="mb-3">
              {errorMessage}
            </Alert>
          )}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Low Temperature Threshold (°C)</Form.Label>
                <Form.Control
                  type="number"
                  name="lowTemp"
                  value={config.lowTemp}
                  onChange={this.handleInputChange}
                  disabled={!config.isEnabled}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>High Temperature Threshold (°C)</Form.Label>
                <Form.Control
                  type="number"
                  name="highTemp"
                  value={config.highTemp}
                  onChange={this.handleInputChange}
                  disabled={!config.isEnabled}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  Duration (minutes) <i className="fa fa-info-circle"></i>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="durationThresholdMinutes"
                  value={config.durationThresholdMinutes}
                  onChange={this.handleInputChange}
                  disabled={!config.isEnabled}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md="4">
              <Form.Group>
                <Form.Label>Fridge Condition</Form.Label>
                <Form.Control
                  as="select"
                  name="fridgeStatus"
                  onChange={this.handleInputChange}
                  value={config.fridgeStatus}
                  disabled={!config.isEnabled}
                >
                  <option>On</option>
                  <option>Off</option>
                  <option>Ignore</option>
                </Form.Control>
              </Form.Group>
            </Col>

            <Col md="4">
              <Form.Group>
                <Form.Label>Ignition Condition</Form.Label>
                <Form.Control
                  as="select"
                  name="engineStatus"
                  onChange={this.handleInputChange}
                  value={config.engineStatus}
                  disabled={!config.isEnabled}
                >
                  <option>On</option>
                  <option>Off</option>
                  <option>Ignore</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Active Days</Form.Label>
            <div className="d-flex flex-wrap" style={{ gap: "14px" }}>
              {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                <Form.Check
                  key={day}
                  id={`${this.props.sensorType}-${day}`}
                  type="checkbox"
                  label={day.substring(0, 3)}
                  checked={(config.activeDays || "").split(",").includes(day)}
                  onChange={() => this.handleDayToggle(day)}
                  disabled={!config.isEnabled}
                />
              ))}
            </div>
            <Form.Text className="text-muted">
              Live Alert monitoring uses the customer&apos;s local timezone.
            </Form.Text>
          </Form.Group>

          <Row className="mb-3">
            <Col md="4">
              <Form.Group>
                <Form.Label>Monitoring Starts</Form.Label>
                <Form.Control type="time" name="startTime" value={config.startTime || "00:00"} onChange={this.handleInputChange} disabled={!config.isEnabled} />
              </Form.Group>
            </Col>
            <Col md="4">
              <Form.Group>
                <Form.Label>Monitoring Ends</Form.Label>
                <Form.Control type="time" name="endTime" value={config.endTime || "23:59"} onChange={this.handleInputChange} disabled={!config.isEnabled} />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Email Notifications</Form.Label>
            <InputGroup>
              <Form.Control
                type="email"
                value={emailInput}
                onChange={this.handleEmailInputChange}
                placeholder="Enter email address"
                disabled={!config.isEnabled}
              />
              <Button
                variant="primary"
                onClick={this.addEmail}
                disabled={!config.isEnabled}
              >
                <i className="fa fa-plus"></i>
              </Button>
            </InputGroup>
            {this.renderEmailList()}
          </Form.Group>

          <div
            className="d-flex align-items-center flex-wrap"
            style={{ gap: "8px" }}
          >
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <Button
                variant="light"
                onClick={this.cancelChanges}
                className="ms-2"
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={this.saveConfig}>
                Save
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }
}

export default SensorConfigPanel;
